import React from "react";
import CodeBlock from "@/components/CodeBlock"; // kendi yoluna göre düzenle

const ControllerDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add controller
      </h2>

      <p className="mb-6">
        The <strong>add controller</strong> command scaffolds a full RESTful API
        controller using the predefined AxisNet controller template. The
        generated controller follows the clean CQRS structure used throughout
        AxisNet and includes GET, LIST, POST, PUT, and DELETE actions wired into
        the Mediator pipeline.
      </p>

      {/* CONTROLLER METHODS OVERVIEW */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Generated Controller Methods — Overview
        </h3>

        <p className="mb-6">
          Each controller generated by AxisNet follows a predefined CQRS-based
          template that includes all essential CRUD endpoints. The structure is
          designed to be consistent across all domain modules and fully aligned
          with the MediatR-driven architecture of AxisNet. Below is a detailed
          explanation of every method scaffolded by the CLI.
        </p>

        {/* GET BY ID */}
        <h4 className="text-xl font-semibold text-white mt-6 mb-2">
          1. GET — Retrieve Single {`{{DomainName}}`}
        </h4>
        <p className="mb-4">
          Fetches a single <strong>{`{{DomainNameLower}}`}</strong> instance
          using its unique ID. This endpoint sends a{" "}
          <code>{`Get{{DomainName}}ByIdQuery`}</code> to MediatR. Intended for
          detail views, entity preview pages, or edit screens.
        </p>

        {/* GET ALL */}
        <h4 className="text-xl font-semibold text-white mt-6 mb-2">
          2. GET — Retrieve Paginated and Filtered List
        </h4>
        <p className="mb-4">
          Returns a paginated, filterable list of{" "}
          <strong>{`{{DomainNameLower}}s`}</strong>. It accepts{" "}
          <code>PageRequest</code> and <code>FilterRequest</code>, then forwards
          them through <code>{`Get{{DomainName}}ListQuery`}</code>. Common
          use-cases include table listings, dashboards, and reporting screens.
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            <strong>withDeleted:</strong> optionally includes soft-deleted
            records
          </li>
          <li>
            <strong>PageRequest:</strong> paging control
          </li>
          <li>
            <strong>FilterRequest:</strong> dynamic filter generation
          </li>
        </ul>

        {/* PUT */}
        <h4 className="text-xl font-semibold text-white mt-6 mb-2">
          3. PUT — Update Existing {`{{DomainName}}`}
        </h4>
        <p className="mb-4">
          Updates an existing entity. The route ID overrides the ID inside the
          request body for safety. The request is handled through the{" "}
          <code>{`Update{{DomainName}}Command`}</code>.
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Ensures consistent ID integrity</li>
          <li>Executes domain-level validation inside the handler</li>
          <li>Triggers cache invalidation when using Redis behaviors</li>
        </ul>

        {/* POST */}
        <h4 className="text-xl font-semibold text-white mt-6 mb-2">
          4. POST — Create a New {`{{DomainName}}`}
        </h4>
        <p className="mb-4">
          Creates a new <strong>{`{{DomainNameLower}}`}</strong>. Incoming data
          is mapped into a <code>{`Create{{DomainName}}Command`}</code> and
          processed by the domain's create handler. Used for forms, onboarding
          flows, or admin creation screens.
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            Returns <strong>201 Created</strong> on success
          </li>
          <li>Executes domain validation before persistence</li>
          <li>Fully compatible with Entity + ValueObject structures</li>
        </ul>

        {/* DELETE */}
        <h4 className="text-xl font-semibold text-white mt-6 mb-2">
          5. DELETE — Remove {`{{DomainName}}`}
        </h4>
        <p className="mb-4">
          Deletes the specified entity using its ID and calls the{" "}
          <code>{`Delete{{DomainName}}Command`}</code>. Depending on project
          configuration, this may perform either:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            <strong>Soft delete</strong> (recommended)
          </li>
          <li>
            <strong>Hard delete</strong> (if overrides are used)
          </li>
        </ul>

        <p>
          The delete endpoint also helps maintain list integrity and can trigger{" "}
          <strong>Redis cache cleanup</strong> when caching behaviors are
          enabled.
        </p>
      </div>

      {/* API VERSIONING */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Optional Versioning
        </h3>

        <p className="mb-4">
          Controllers can optionally include an API version using the{" "}
          <code>--version</code> flag:
        </p>

        <CodeBlock
          code={`axisnet add controller --name Employee --version 1.0`}
        />

        <p className="mt-4">
          If <strong>--version</strong> is not provided, but{" "}
          <code>use-controller-versioning</code> flag is ON, AxisNet defaults to
          <strong>1.0</strong>.
        </p>

        <CodeBlock language="csharp" code={`[ApiVersion("1.0")]`} />
      </div>

      {/* FEATURE TRIGGER */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Automatic Trigger via add feature
        </h3>

        <p className="mb-4">
          The controller generator is automatically invoked when running:
        </p>

        <CodeBlock code={`axisnet add feature --name Employees --controller`} />

        <p className="mt-4">This produces a full feature module including:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            Domain entity (if <code>--domain</code> is used)
          </li>
          <li>
            Repository (if <code>--repository</code> is used)
          </li>
          <li>API controller</li>
          <li>
            Test boilerplates (if <code>--test</code> is used)
          </li>
        </ul>
      </div>

      {/* TEMPLATE REPLACEMENT */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Template Processing Logic
        </h3>

        <p className="mb-4">
          ControllerGenerator replaces several template tokens:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            <code>{`{{ DomainName }}`}</code> → Employee
          </li>
          <li>
            <code>{`{{ ProjectName }}`}</code> → read from axisnet.json
          </li>
          <li>
            <code>{`{{ Version }}`}</code> → v1 / 1.0 / placeholder
          </li>
        </ul>

        <p className="mt-3">
          These are passed into <code>BaseGenerator.Generate()</code> and then
          written into <code>/Controllers</code>.
        </p>
      </div>

      {/* VERSIONING FLAG SECTION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Versioning Flag — use-controller-versioning
        </h3>

        <p className="mb-4">
          The <code>use-controller-versioning</code> flag enables automatic API
          versioning when generating controllers. If the developer does not
          specify a version using the <code>--version</code> parameter, AxisNet
          automatically inserts a default version of <strong>1.0</strong>.
        </p>

        <h4 className="text-lg font-semibold text-white mb-2">How It Works</h4>

        <p className="mb-4">
          When generating a controller, the CLI evaluates the version in the
          following order:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            If <code>--version</code> is provided → use that version
          </li>
          <li>
            Else if <code>use-controller-versioning</code> is ON → use
            <strong>1.0</strong>
          </li>
          <li>
            Else → keep the template placeholder <code>{`{{Version}}`}</code>
          </li>
        </ul>

        <h4 className="text-lg font-semibold text-white mb-2">
          Default Behavior (Flag ON)
        </h4>

        <CodeBlock
          language="bash"
          code={`axisnet add controller --name Employee`}
        />

        <p className="mt-4">Generates:</p>

        <CodeBlock
          language="csharp"
          code={`[ApiVersion("1.0")]
[Route("api/v{version:apiVersion}/[controller]")]
public class EmployeeController : AxisNetBaseController
{
    // ...
}`}
        />

        <h4 className="text-lg font-semibold text-white mt-6 mb-2">
          With Explicit Version
        </h4>

        <CodeBlock
          language="bash"
          code={`axisnet add controller --name Employee --version v2`}
        />

        <p className="mt-4">Generates:</p>

        <CodeBlock
          language="csharp"
          code={`[ApiVersion("v2")]
[Route("api/v{version:apiVersion}/[controller]")]
public class EmployeeController : AxisNetBaseController
{
    // ...
}`}
        />

        <h4 className="text-lg font-semibold text-white mt-6 mb-2">
          Flag OFF (Placeholder Mode)
        </h4>

        <p className="mb-4">
          If versioning is disabled and no version is supplied, the template
          keeps the version placeholder for deferred processing.
        </p>

        <CodeBlock language="csharp" code={`[ApiVersion("{{Version}}")]`} />

        <p className="mt-4">
          This behavior is useful if versioning is handled later by project-wide
          conventions.
        </p>
      </div>

      {/* OVERWRITE BEHAVIOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Overwrite Protection
        </h3>

        <p className="mb-4">
          If a controller already exists, behavior depends on:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>confirm-overwrite OFF:</strong> overwrite silently
          </li>
          <li>
            <strong>confirm-overwrite ON:</strong> ask before overwriting
          </li>
        </ul>
      </div>
    </section>
  );
};

export default ControllerDoc;
